import { Box, VStack, Grid, Separator, HStack, Text } from "@chakra-ui/react";
import SectionHeader from "../../CustomUi/SectionHeader";
import { BiPlus, BiWallet } from "react-icons/bi";
import { useSchoolsContext } from "../../../Contexts/schoolContext";
import { useEffect, useState } from "react";
import Loader from "../../CustomUi/Loader";
import CustomAlert from "../../CustomUi/CustomAlert";
import Empty from "../../CustomUi/Empty";
import Cad from "../../CustomUi/Cad";
import { useDisclosure } from "@chakra-ui/react";
import { walletService, WalletDto } from "../../../services/walletService";
import useAxios from "../../../hooks/useAxios";

function WalletsPage() {
    const { school, accentColor } = useSchoolsContext();
    const axiosClient = useAxios();
    const [wallets, setWallets] = useState<WalletDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { onOpen } = useDisclosure(); // TODO: Use onOpen when implementing create modal

    useEffect(() => {
        if (school?.id) {
            loadWallets();
        }
    }, [school]);

    const loadWallets = async () => {
        if (!school?.id) return;

        try {
            setIsLoading(true);
            const response = await walletService.getSchoolWallets(axiosClient, school.id);
            if (response.succeeded) {
                setWallets(response.data || []);
            } else {
                setError(response.message);
            }
        } catch (err) {
            setError("Failed to load wallets");
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <CustomAlert message={error} handleClick={loadWallets} />;
    }

    return (
        <VStack gap={8}>
            <Cad>
                <VStack gap={6} align="stretch">
                    <SectionHeader
                        title="School Wallets"
                        subtitle="Manage your school's financial wallets"
                        icon={BiWallet}
                        buttonText="Add Wallet"
                        onButtonClick={onOpen}
                        buttonIcon={BiPlus}
                        buttonColorScheme={accentColor}
                    />

                    <Separator />

                    {wallets.length > 0 ? (
                        <Grid
                            templateColumns={{
                                base: "1fr",
                                md: "repeat(2, 1fr)",
                                xl: "repeat(3, 1fr)",
                            }}
                            gap={6}
                        >
                            {wallets.map((wallet) => (
                                <Cad key={wallet.id}>
                                    <VStack align="stretch" gap={3}>
                                        <HStack justify="space-between">
                                            <Text fontWeight="bold" fontSize="lg">
                                                {wallet.name}
                                            </Text>
                                            {wallet.isMainWallet && (
                                                <Text fontSize="xs" colorScheme="brand" px={2} py={1} borderRadius="md" bg="brand.100">
                                                    Main
                                                </Text>
                                            )}
                                        </HStack>
                                        <Text fontSize="sm" color="gray.600">
                                            {wallet.description}
                                        </Text>
                                        <Text fontSize="2xl" fontWeight="bold" color="brand.500">
                                            ₦{wallet.balance.toLocaleString()}
                                        </Text>
                                        {wallet.feeCategoryName && (
                                            <Text fontSize="xs" color="gray.500">
                                                Category: {wallet.feeCategoryName}
                                            </Text>
                                        )}
                                    </VStack>
                                </Cad>
                            ))}
                        </Grid>
                    ) : (
                        <Box py={8}>
                            <Empty
                                message="No wallets created yet"
                                hasButton
                                buttonText="Create First Wallet"
                                handleClick={onOpen}
                            />
                        </Box>
                    )}
                </VStack>
            </Cad>
        </VStack>
    );
}

export default WalletsPage;
